import {Component, OnDestroy, OnInit} from '@angular/core';
import {Menu} from '../../interface/menu';
import {MenuService} from '../../service/menu.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {GetPageUtil} from '../get-page-util';
import {MenuShowQrModalComponent} from './menu-show-qr-modal/menu-show-qr-modal.component';
import {DeleteMenuDialogComponent} from './delete-menu-dialog/delete-menu-dialog.component';
import {SnackBarUtil} from '../../qr-copy-message/snack-bar-util.service';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {MenuBackendResponse, MenuPageResponse, PurchaseHistoryItem, PurchaseHistoryPageResponse} from '../../service/menu.interfaces';
import {FilterOptions, MenuFilterRequest} from '../../interface/menuFilterRequest';
import {MenuFilterDialogComponent, MenuFilterDialogData, MenuFilterDialogResult} from './menu-filter-dialog/menu-filter-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {Language} from '../../models/language';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit, OnDestroy {
  menus: Menu[] = [];
  currentPage: number = 0;
  pageSize: number = 10;
  totalMenus: number = 0;
  totalPages: number = 0;

  purchaseHistoryItems: PurchaseHistoryItem[] = [];
  purchaseHistoryPage: number = 0;
  purchaseHistoryTotalItems: number = 0;
  purchaseHistoryTotalPages: number = 0;

  isLoading: boolean = false;
  activeTab: 'all' | 'purchase-history' = 'all';
  searchQuery: string = '';
  currentLang: string = '';
  protected readonly Language = Language;

  allMenusFilters: MenuFilterRequest = {};
  purchaseHistoryFilters: MenuFilterRequest = {};

  private cardUsernames: string[] = [];
  private searchSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private getPageUtil = new GetPageUtil();

  constructor(
    private menuService: MenuService,
    private dialog: MatDialog,
    private snackBarUtil: SnackBarUtil,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.currentLang = this.translateService.currentLang || Language.HY;
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => { this.currentLang = event.lang; });

    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.purchaseHistoryPage = 0;
      this.loadCurrentTab();
    });

    this.menuService.getAllCardUsernames().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: usernames => { this.cardUsernames = usernames; },
      error: () => {}
    });

    this.loadMenus();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentTab() {
    if (this.activeTab === 'all') {
      this.loadMenus();
    } else {
      this.loadPurchaseHistory();
    }
  }

  loadMenus() {
    this.isLoading = true;
    const params: MenuFilterRequest = {
      ...this.allMenusFilters,
      ...(this.searchQuery.trim() ? {search: this.searchQuery.trim()} : {})
    };

    this.menuService.searchAllMenus(params, this.currentPage, this.pageSize).subscribe({
      next: (response: MenuPageResponse) => {
        const content = response.content;
        this.menus = content.map((menu: MenuBackendResponse) => ({
          id: menu.id,
          menu_name: menu.menuName || menu.menu_name || '',
          business_name: menu.businessName || menu.business_name,
          address: menu.address,
          linked_users: menu.linkedUserCardUsernames || menu.linked_users || [],
          is_active: menu.isActive !== undefined ? menu.isActive : menu.is_active,
          created_date: menu.createdDate || menu.created_date
        }));
        this.totalMenus = response.totalElements;
        this.totalPages = response.totalPages;
        this.currentPage = response.number;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  loadPurchaseHistory() {
    this.isLoading = true;
    const params: MenuFilterRequest = {
      ...this.purchaseHistoryFilters,
      ...(this.searchQuery.trim() ? {search: this.searchQuery.trim()} : {})
    };

    this.menuService.getPurchaseHistory(params, this.purchaseHistoryPage, this.pageSize).subscribe({
      next: (response: PurchaseHistoryPageResponse) => {
        this.purchaseHistoryItems = response.content || [];
        this.purchaseHistoryTotalItems = response.totalElements || this.purchaseHistoryItems.length;
        this.purchaseHistoryTotalPages = response.totalPages || Math.ceil(this.purchaseHistoryTotalItems / this.pageSize);
        this.purchaseHistoryPage = response.number ?? this.purchaseHistoryPage;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        if (error.status !== 404) {
          this.snackBarUtil.openSnackBar({
            data: {message: this.translateService.instant('menu_management.errors.purchase_history_load')},
            duration: 3000,
            panelClass: SnackBarUtil.CLASSES.error,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
        }
      }
    });
  }

  selectTab(tab: 'all' | 'purchase-history') {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.searchQuery = '';
    if (tab === 'all') {
      this.currentPage = 0;
      this.loadMenus();
    } else {
      this.purchaseHistoryPage = 0;
      this.loadPurchaseHistory();
    }
  }

  onSearch() {
    this.searchSubject.next();
  }

  clearSearch() {
    this.searchQuery = '';
    this.currentPage = 0;
    this.purchaseHistoryPage = 0;
    this.loadCurrentTab();
  }

  openFilterDialog() {
    const filterOptions: FilterOptions = {
      businessNames: [...new Set(this.menus.map(m => m.business_name).filter((v): v is string => !!v))],
      menuNames: [...new Set(this.menus.map(m => m.menu_name).filter(Boolean))],
      addresses: [...new Set(this.menus.map(m => m.address).filter((v): v is string => !!v))],
      cardUsernames: this.cardUsernames
    };

    const currentFilters: MenuFilterRequest =
      this.activeTab === 'all' ? {...this.allMenusFilters} : {...this.purchaseHistoryFilters};

    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'menu-filter-dialog-panel';
    dialogConfig.width = '380px';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = {activeTab: this.activeTab, filterOptions, currentFilters} as MenuFilterDialogData;

    this.dialog.open(MenuFilterDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((result: MenuFilterDialogResult | null) => {
        if (!result) return;
        const filters = result.action === 'reset' ? {} : result.filters;
        if (this.activeTab === 'all') {
          this.allMenusFilters = filters;
          this.currentPage = 0;
          this.loadMenus();
        } else {
          this.purchaseHistoryFilters = filters;
          this.purchaseHistoryPage = 0;
          this.loadPurchaseHistory();
        }
      });
  }

  get isFilterActive(): boolean {
    const f = this.activeTab === 'all' ? this.allMenusFilters : this.purchaseHistoryFilters;
    return Object.values(f).some(v =>
      v !== undefined && v !== null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== '')
    );
  }

  openCreateMenuDialog() {
    this.router.navigate(['/menu/add']);
  }

  openEditMenuDialog(menu: Menu) {
    this.router.navigate(['/menu/edit', menu.id]);
  }

  openQrModal(menu: Menu) {
    this.isLoading = true;
    this.menuService.generateMenuQrCode(menu.id).subscribe({
      next: (qrCodeBase64: string) => {
        this.isLoading = false;
        const menuUrl = `${window.location.origin}/menu/view/${menu.id}`;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '90vw';
        dialogConfig.disableClose = false;
        dialogConfig.panelClass = 'qr-dialog';
        dialogConfig.data = {
          menuName: menu.menu_name,
          address: menu.address || '',
          qrCodeBase64: qrCodeBase64,
          menuUrl: menuUrl,
          menuId: menu.id
        };
        this.dialog.open(MenuShowQrModalComponent, dialogConfig);
      },
      error: () => {
        this.isLoading = false;
        this.snackBarUtil.openSnackBar({
          data: {message: this.translateService.instant('menu_management.errors.qr_generate')},
          duration: 3000,
          panelClass: SnackBarUtil.CLASSES.error,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }

  openDeleteDialog(menu: Menu) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '400px';
    dialogConfig.data = {menuName: menu.menu_name, menuId: menu.id};

    this.dialog.open(DeleteMenuDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) this.deleteMenu(menu.id);
      });
  }

  deleteMenu(menuId: number) {
    this.isLoading = true;
    this.menuService.deleteMenu(menuId).subscribe({
      next: () => {
        this.isLoading = false;
        this.snackBarUtil.openSnackBar({
          data: {message: this.translateService.instant('menu_management.messages.delete_success')},
          duration: 3000,
          panelClass: SnackBarUtil.CLASSES.success,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
        this.loadMenus();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        const message = error.status === 404 ? this.translateService.instant('menu_management.errors.not_found')
          : error.status === 409 ? this.translateService.instant('menu_management.errors.delete_active_links')
          : this.translateService.instant('menu_management.errors.delete_failed');
        this.snackBarUtil.openSnackBar({
          data: {message},
          duration: 3000,
          panelClass: SnackBarUtil.CLASSES.error,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadMenus();
    }
  }

  goToPurchaseHistoryPage(page: number) {
    if (page >= 0 && page < this.purchaseHistoryTotalPages) {
      this.purchaseHistoryPage = page;
      this.loadPurchaseHistory();
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.currentPage, this.totalMenus, this.pageSize);
  }

  getPurchaseHistoryPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.purchaseHistoryPage, this.purchaseHistoryTotalItems, this.pageSize);
  }

  formatPurchasedProductsDisplay(products?: string): string {
    if (!products?.trim()) {
      return '-';
    }
    const trimmed = products.trim();
    const count = trimmed.split(',').map(part => part.trim()).filter(Boolean).length;
    return count > 0 ? `${trimmed} (${count})` : trimmed;
  }

  protected readonly Math = Math;
}
